import { CloseOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Card, Col, Form, Input, message, Row, Upload } from 'antd'
import { IBaseFormProps } from 'Modules'

import { ALLOWED_MIME_TYPES_IMAGE } from 'constants/mime-type'
import { INITIAL_CO_TRUSTED_PARTNER } from 'extends/pto-v2.extend'
import useUploadFile from 'hooks/use-upload-file'

const MAX_PARTNERS = 10
const MAX_LOGO_SIZE_MB = 10

interface ICoTrustedPartnersProps extends IBaseFormProps {}

interface IUploadPartnerLogoProps {
  currentLogo?: string
  edit: boolean
  onUploadSuccess: (filePath: string) => void
  onRemove: () => void
}

const UploadPartnerLogo = ({
  currentLogo,
  edit,
  onUploadSuccess,
  onRemove,
}: IUploadPartnerLogoProps) => {
  const { singleFile, handleUploadSingle } = useUploadFile({
    mimeTypes: ALLOWED_MIME_TYPES_IMAGE,
    onUploadSuccess: (data) => {
      onUploadSuccess(data.filePath)
    },
  })

  const beforeUpload = (file: File) => {
    if (!ALLOWED_MIME_TYPES_IMAGE.includes(file.type)) {
      message.error('You can only upload image file!')
      return false
    }
    if (file.size / 1024 / 1024 > MAX_LOGO_SIZE_MB) {
      message.error(`Image must be smaller than ${MAX_LOGO_SIZE_MB}MB!`)
      return false
    }
    return true
  }

  const isUploading = singleFile.length > 0

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      {currentLogo && (
        <div style={{ position: 'relative' }}>
          <img
            src={currentLogo}
            alt="Partner logo"
            style={{
              width: 100,
              height: 100,
              objectFit: 'contain',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
            }}
          />
          {edit && (
            <CloseOutlined
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                color: '#fff',
                fontSize: 12,
                cursor: 'pointer',
                background: '#ff4d4f',
                borderRadius: '50%',
                padding: 4,
              }}
              onClick={onRemove}
            />
          )}
        </div>
      )}
      {edit && !currentLogo && (
        <Upload
          showUploadList={false}
          customRequest={handleUploadSingle}
          beforeUpload={beforeUpload}
          accept={ALLOWED_MIME_TYPES_IMAGE.join(',')}
        >
          <Button icon={<UploadOutlined />} loading={isUploading}>
            Upload Logo
          </Button>
        </Upload>
      )}
    </div>
  )
}

const CoTrustedPartners = ({ form, edit = true }: ICoTrustedPartnersProps) => {
  const handleUploadSuccess = (index: number, filePath: string) => {
    const partners = form.getFieldValue('coTrustedPartners') || []
    partners[index] = {
      ...partners[index],
      logo: filePath,
    }
    form.setFieldsValue({ coTrustedPartners: partners })
    form.validateFields([['coTrustedPartners', index, 'logo']])
  }

  const handleRemoveLogo = (index: number) => {
    const partners = form.getFieldValue('coTrustedPartners') || []
    partners[index] = {
      ...partners[index],
      logo: '',
    }
    form.setFieldsValue({ coTrustedPartners: partners })
  }

  return (
    <fieldset>
      <legend>Partners</legend>
      <Form.List name="coTrustedPartners">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => {
              const partners = form.getFieldValue('coTrustedPartners') || []
              const currentLogo = partners[index]?.logo

              return (
                <Card
                  key={key}
                  size="small"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{`Partner #${index + 1}`}</span>
                      {edit && fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      )}
                    </div>
                  }
                  style={{ marginBottom: 16, borderRadius: 16, padding: 8 }}
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item {...restField} label="Name" name={[name, 'name']}>
                        <Input disabled={!edit} maxLength={50} placeholder="Enter partner name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label="Website Link"
                        name={[name, 'website']}
                        rules={[{ type: 'url', message: 'Please enter a valid website link!' }]}
                      >
                        <Input
                          disabled={!edit}
                          maxLength={300}
                          placeholder="https://partner-website.com"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item {...restField} label="Logo" name={[name, 'logo']}>
                        <UploadPartnerLogo
                          currentLogo={currentLogo}
                          edit={edit}
                          onUploadSuccess={(filePath) => handleUploadSuccess(index, filePath)}
                          onRemove={() => handleRemoveLogo(index)}
                        />
                      </Form.Item>
                      <p style={{ marginTop: -8, color: '#999', fontSize: 12 }}>
                        You can only upload 1 image. Max size: {MAX_LOGO_SIZE_MB} MB.
                      </p>
                    </Col>
                  </Row>
                </Card>
              )
            })}
            {edit && fields.length < MAX_PARTNERS && (
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add({ ...INITIAL_CO_TRUSTED_PARTNER })}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Partner ({fields.length}/{MAX_PARTNERS})
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>
    </fieldset>
  )
}

export default CoTrustedPartners
